import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { AiControlSystem, AiEditableSystemKey, AiEditingState, AiInstructionVersions } from "../../api/types";
import { money } from "../../i18n/intl";
import { Modal } from "../Modal";
import { fmtAiWhen } from "./aiControlUi";

interface Props {
  systemKey: AiEditableSystemKey;
  system: AiControlSystem;
  editing: AiEditingState;
  onChanged: () => void;
  onTest: () => void;
}

/**
 * G-149 S2 — every version of a system's editable instructions, newest
 * first, the text in code last; each with what the ledger says it answered
 * and cost. Any version but the live one can be put back.
 */
export function AiInstructionVersionsList({ systemKey, system, editing, onChanged, onTest }: Props) {
  const [data, setData] = useState<AiInstructionVersions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .aiInstructionVersions(systemKey)
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the versions.");
      });
    return () => {
      cancelled = true;
    };
  }, [systemKey, reloadTick, editing.live.number]);

  const titles = new Map(system.parts.filter((p) => p.key !== null).map((p) => [p.key as string, p.title ?? p.key]));
  const target = data?.versions.find((v) => v.number === confirm) ?? null;

  async function makeLive(number: number) {
    setBusy(true);
    setError(null);
    try {
      await api.makeAiInstructionsLive(systemKey, number);
      onChanged();
      setConfirm(null);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ai-control-versions" aria-label="Versions">
      {error ? <div className="alert error">{error}</div> : null}
      {!data && !error ? <p className="muted">Loading…</p> : null}

      {editing.draft ? (
        <div className="ai-control-version is-draft">
          <div className="ai-control-version-tag">
            <span className="ai-control-pill is-warning">Draft</span>
          </div>
          <div className="ai-control-version-main">
            <span>{editing.draft.note ?? "Not named yet"}</span>
            <span className="muted">
              {editing.draft.changedKeys.map((k) => titles.get(k) ?? k).join(" · ") || "No change from live"}
            </span>
          </div>
          <div className="ai-control-version-when muted">
            {fmtAiWhen(editing.draft.savedAt)}
            {editing.draft.savedBy ? <br /> : null}
            {editing.draft.savedBy}
          </div>
          <div className="ai-control-version-act">
            {editing.canTest && editing.draft.changedKeys.length > 0 ? (
              <button type="button" className="button tinted" onClick={onTest}>
                Test it
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {data?.versions.map((version) => (
        <div key={version.number} className={`ai-control-version${version.live ? " is-live" : ""}`}>
          <div className="ai-control-version-tag">
            <span className={`ai-control-pill ${version.live ? "is-live" : "is-muted"}`}>
              {version.live ? "Live" : version.number === 0 ? "In code" : "Earlier"}
            </span>
            <span className="ai-control-mono muted">{version.version}</span>
          </div>
          <div className="ai-control-version-main">
            <span>{version.number === 0 ? "Text in code" : version.note}</span>
            <span className="muted">
              {version.calls} {version.calls === 1 ? "call" : "calls"} · {money(version.costEur)}
            </span>
            <details className="ai-control-version-text">
              <summary>Text</summary>
              {Object.entries(version.texts).map(([key, text]) => (
                <div key={key} className="ai-control-version-part">
                  <strong>{titles.get(key) ?? key}</strong>
                  <div className="ai-control-text">{text}</div>
                </div>
              ))}
            </details>
          </div>
          <div className="ai-control-version-when muted">
            {fmtAiWhen(version.createdAt)}
            {version.createdBy ? <br /> : null}
            {version.createdBy}
          </div>
          <div className="ai-control-version-act">
            {!version.live ? (
              <button type="button" className="button plain" onClick={() => setConfirm(version.number)} disabled={busy}>
                {data && version.number > data.liveNumber ? "Put live again" : "Roll back to this"}
              </button>
            ) : null}
          </div>
        </div>
      ))}

      {target ? (
        <Modal title="Put this version live?" onClose={() => setConfirm(null)}>
          <p>
            <strong>{target.number === 0 ? "Text in code" : target.note}</strong>{" "}
            <span className="ai-control-mono muted">{target.version}</span>
          </p>
          <div className="ai-control-actions">
            <button type="button" className="button plain" onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="button" onClick={() => void makeLive(target.number)} disabled={busy}>
              {busy ? "Putting it live…" : "Put live"}
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
